import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Card, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import { getTranslation } from '../../../account/services/auth'
import { TopupWristband, TopupWristbandPayload } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import {
  createTopup,
  getWristbandTopups,
} from '../../../core/services/topup-wristbands'
import translate from '../../../core/services/translate'
import { MessageBox } from '../messages/MessageBox'

export interface Props {
  visible: boolean
  setVisible: (show: boolean) => void
  size: number
  page: number
  setItems: (i: TopupWristband[]) => void
  setTotalElements: (e: number) => void
}

interface IFormValues {
  topUpOn: string
  ticketCode?: string | null
  wristbandId?: number | null
  topUpType: boolean
  topUpAmount: number
}

export const AddTopupWristband = (props: Props): JSX.Element => {
  const { visible, setVisible, page, size, setItems, setTotalElements } = props

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const handleClose = () => {
    setVisible(false)
    reset()
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [])

  const schema = yup.object().shape(
    {
      topUpOn: yup
        .string()
        .oneOf(['Barcode', 'Wristband SN'])
        .required('Top Up On is required'),
      wristbandId: yup.number().when('topUpOn', {
        is: (val: string) => (val === 'Wristband SN' ? true : false),
        then: (schema) => schema.required('Serial Number is required'),
        otherwise: (schema) =>
          schema
            .optional()
            .nullable()
            .transform((val) => (!isNaN(val) ? val : null)),
      }),
      ticketCode: yup.string().when('topUpOn', {
        is: (val: string) => (val === 'Barcode' ? true : false),
        then: (schema) => schema.required('Barcode is required'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      topUpType: yup.boolean().required('Top Up Type is required'),
      topUpAmount: yup.number().required('Top Up Amount is required'),
    },
    [
      ['topUpOn', 'ticketCode'],
      ['topUpOn', 'wristbandId'],
    ],
  )

  const {
    handleSubmit,
    register,
    reset,
    watch,
    control,
    formState: { errors, dirtyFields },
  } = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IFormValues>(schema),
  })

  const topupOnWatch = watch('topUpOn')

  const language = getTranslation()

  const submit = async (values: IFormValues) => {
    try {
      const data: TopupWristbandPayload = {
        amount: values.topUpAmount,
        reward: values.topUpType,
        ticketCode: values.ticketCode,
        wristbandId: values.wristbandId,
      }
      const resp = await createTopup(data)
      const response = await getWristbandTopups({
        page,
        size,
      })
      if (resp.status === 200 && response.status === 200) {
        setItems(response.data.content)
        setTotalElements(response.data.totalElements)
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Create Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      handleClose()
    }
  }

  return (
    <>
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title={translate(
                language.value,
                'topUpWristband.topUpWristbandBarcode',
              )}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2 justify-end">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              form="topup-form"
              variant="primary"
              disabled={Object.keys(dirtyFields).length === 0}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="topup-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <Card className="p-4 space-y-7">
            <FormGroup
              label={translate(language.value, 'columns.topUpOn')}
              tooltip={translate(language.value, 'columns.topUpOn')}
            >
              <Controller
                control={control}
                name="topUpOn"
                render={({ field }) => (
                  <SelectField
                    outline
                    value={field.value}
                    onValueChange={field.onChange}
                    error={errors?.topUpOn?.message}
                    placeholder={translate(language.value, 'columns.topUpOn')}
                    options={map(['Wristband SN', 'Barcode'], (one) => ({
                      label: one,
                      value: one,
                    }))}
                  />
                )}
              />
            </FormGroup>

            <FormGroup
              label={translate(
                language.value,
                'topUpWristband.enterSerialNumberBarcode',
              )}
            >
              {topupOnWatch === 'Barcode' ? (
                <Input
                  isInvalid={!!errors?.ticketCode?.message}
                  {...register('ticketCode')}
                  type="text"
                  placeholder={translate(
                    language.value,
                    'topUpWristband.enterSerialNumberBarcode',
                  )}
                />
              ) : (
                <Input
                  isInvalid={!!errors?.wristbandId?.message}
                  {...register('wristbandId')}
                  type="text"
                  placeholder={translate(
                    language.value,
                    'topUpWristband.enterSerialNumberBarcode',
                  )}
                />
              )}
            </FormGroup>

            <FormGroup
              label={translate(language.value, 'topUpWristband.topUpType')}
              tooltip={translate(language.value, 'topUpWristband.topUpType')}
            >
              <Controller
                control={control}
                name="topUpType"
                render={({ field }) => (
                  <SelectField
                    outline
                    value={field.value ? '1' : '0'}
                    onValueChange={field.onChange}
                    error={errors?.topUpType?.message}
                    placeholder={translate(
                      language.value,
                      'topUpWristband.topUpType',
                    )}
                    options={map(
                      [
                        { name: 'Reward', value: '1' },
                        { name: 'Online', value: '0' },
                      ],
                      (one) => ({
                        label: one.name,
                        value: one.value,
                      }),
                    )}
                  />
                )}
              />
            </FormGroup>

            <FormGroup
              label={translate(language.value, 'topUpWristband.topUpAmount')}
              tooltip={translate(language.value, 'topUpWristband.topUpAmount')}
            >
              <Input
                isInvalid={!!errors?.topUpAmount?.message}
                {...register('topUpAmount')}
                type="number"
                min={0}
                placeholder={translate(
                  language.value,
                  'topUpWristband.topUpAmount',
                )}
              />
            </FormGroup>
          </Card>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
